/**
 * Affiliates → Recruit: who to invite into the program next. Paying
 * customers who used SyncSuite in the last 14 days and aren't affiliates yet,
 * most active first. "Invite" adds them as a prospect with an invite move on
 * today's list and a message to copy (AI on that click, else a template).
 * Nothing is ever sent from here.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Activity, Mail, MailPlus, Sparkles, UserCheck, UserPlus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Avatar, Button, Modal, ModalFooter, Skeleton, Textarea, useToast } from "@/components/ui";
import { Empty, Pill, Section, dayLabel, timeAgo, usd } from "@/features/command/ui";
import { CopyButton, STATUS_META } from "./shared";
import type { InvitedPerson, RecruitCandidate } from "./types";

interface Invite { name: string; email: string | null; affiliateId: string; moveId: string | null; message: string; via: "ai" | "rules" | "saved" }

export function RecruitTab({ onInvited }: { onInvited: () => void }) {
  const toast = useToast();
  const { pathname, search } = useLocation();
  const from = { from: pathname + search };
  const [data, setData] = useState<{ candidates: RecruitCandidate[]; invited: InvitedPerson[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<Invite | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<{ candidates: RecruitCandidate[]; invited: InvitedPerson[] }>("/affiliates/recruit"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load who to invite");
      setData((d) => d ?? { candidates: [], invited: [] });
    }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const invite = async (c: RecruitCandidate) => {
    setBusy(c.account_id);
    try {
      const r = await api.post<{ affiliate_id: string; move_id: string | null; message: string; via: "ai" | "rules" }>(`/affiliates/recruit/${c.account_id}/invite`);
      setOpen({ name: c.name, email: c.email, affiliateId: r.affiliate_id, moveId: r.move_id, message: r.message, via: r.via });
      toast.success(`${c.name} is now a prospect`, "Nothing was sent. Copy the message to them.");
      onInvited();
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't invite them");
      void load();
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-zinc-400">
        Paying customers who used SyncSuite in the last 14 days and aren't affiliates yet, most active first. Happy customers make the best affiliates.
      </p>

      <Section title="Who to invite" icon={UserPlus} count={data?.candidates.length} bodyClassName="p-0">
        {data == null ? (
          <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
        ) : data.candidates.length === 0 ? (
          <Empty icon={UserCheck} title="Nobody to invite right now">
            Everyone who paid and used SyncSuite in the last 14 days is already on your affiliates list. New paying customers show up here by themselves.
          </Empty>
        ) : (
          <ul className="divide-y divide-zinc-800/60">
            {data.candidates.map((c) => (
              <li key={c.account_id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
                <Link to={c.contact_id ? `/contacts/${c.contact_id}` : `/accounts/${c.account_id}`} state={from} className="group flex min-w-0 flex-1 items-center gap-3">
                  <Avatar name={c.name} email={c.email} size="sm" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-zinc-100 group-hover:text-indigo-300">{c.name}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500">
                      {c.plan_code && <span className="capitalize">{c.plan_code}</span>}
                      {c.first_paid_at && <span>paying since {dayLabel(c.first_paid_at, { month: "short", year: "numeric" })}</span>}
                      {c.revenue > 0 && <span>{usd(c.revenue)} paid</span>}
                    </span>
                  </span>
                </Link>
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="flex items-center gap-1.5 text-xs text-zinc-400" title="Things they did in SyncSuite in the last 14 days">
                    <Activity className="h-3.5 w-3.5 text-indigo-300" />
                    {c.events_14d} {c.events_14d === 1 ? "action" : "actions"} in 14 days
                    {c.last_active_at && <span className="text-zinc-500">· active {timeAgo(c.last_active_at)}</span>}
                  </span>
                  <Button size="sm" loading={busy === c.account_id} disabled={!!busy && busy !== c.account_id} leftIcon={<MailPlus className="h-3.5 w-3.5" />} onClick={() => void invite(c)}>
                    Invite
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {data && data.invited.length > 0 && (
        <Section title="Invited" icon={MailPlus} count={data.invited.length} bodyClassName="p-0">
          <ul className="divide-y divide-zinc-800/60">
            {data.invited.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                <Avatar name={p.name} email={p.email} size="xs" />
                <Link to={`/affiliates/${p.id}`} state={from} className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-200 hover:text-indigo-300">{p.name}</Link>
                {p.joined
                  ? <Pill color="emerald">Joined the program</Pill>
                  : <Pill color={STATUS_META[p.status]?.color ?? "zinc"}>{STATUS_META[p.status]?.label ?? p.status}</Pill>}
                <span className="hidden shrink-0 text-xs text-zinc-500 sm:inline">invited {timeAgo(p.invited_at)}</span>
                {p.message && !p.joined && (
                  <Button size="sm" variant="ghost" onClick={() => setOpen({ name: p.name, email: p.email, affiliateId: p.id, moveId: p.move_id, message: p.message!, via: "saved" })}>
                    Message
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <InviteModal invite={open} onClose={() => setOpen(null)} onEdited={() => void load()} />
    </div>
  );
}

function InviteModal({ invite, onClose, onEdited }: { invite: Invite | null; onClose: () => void; onEdited: () => void }) {
  const { pathname, search } = useLocation();
  const toast = useToast();
  const [text, setText] = useState("");
  const saved = useRef("");
  useEffect(() => { if (invite) { setText(invite.message); saved.current = invite.message; } }, [invite]);
  /** Keep an edited message on the invite move, so the moves list has the same words. */
  const persist = async () => {
    if (!invite?.moveId || text === saved.current) return;
    try {
      await api.patch(`/affiliates/moves/${invite.moveId}`, { draft: text });
      saved.current = text;
      onEdited();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save your edit");
    }
  };
  const close = () => { void persist(); onClose(); };
  const first = invite?.name.split(/\s+/)[0] ?? "";
  const mailto = invite?.email ? `mailto:${encodeURIComponent(invite.email)}?subject=${encodeURIComponent("Earn with SyncSuite")}&body=${encodeURIComponent(text)}` : null;
  return (
    <Modal open={!!invite} onClose={close} size="md" title={`Invite ${first}`} description="Nothing has been sent. Copy this to them by email, text or DM.">
      <div className="space-y-2">
        {invite?.via === "ai" && <p className="flex items-center gap-1.5 text-[11px] text-zinc-500"><Sparkles className="h-3 w-3" />Written by AI. Read it over first.</p>}
        <Textarea value={text} onChange={(e) => setText(e.target.value)} onBlur={() => void persist()} rows={7} className="text-[13px] leading-relaxed" />
        <div className="flex flex-wrap items-center gap-1.5">
          <CopyButton text={text} label="Copy message" onCopy={() => void persist()} />
          {mailto && (
            <a href={mailto} className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100">
              <Mail className="h-3.5 w-3.5" /> Open in my email
            </a>
          )}
        </div>
        <p className="text-[11px] text-zinc-500">It's also on today's moves as "Invite them into the program", with this message, so it won't slip.</p>
      </div>
      <ModalFooter>
        {invite && <Link to={`/affiliates/${invite.affiliateId}`} state={{ from: pathname + search }} onClick={close}><Button variant="ghost">Open their page</Button></Link>}
        <Button onClick={close}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}
